import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SpinnerWithLoadMask from "components/SpinnerWithLoadMask";
import ContentHeader from "layout/discovery/content/ContentHeader";
import { EmptyContent } from "layout/dictionarySearch/content/helpers/renderHelper";
import { getContent } from "slices/discovery/actions/content";
import {
  setByThemeContentPage,
  setPreviousDocItems,
} from "slices/discovery/discoverySlice";
import PropTypes from "prop-types";
import { arraysEqual, scrollToTop } from "utils";
import { ITEMS_PER_PAGE } from "constants/app";
import { selectConfig, selectContent } from "slices/discovery/selectors";
import { useColumnsResizing } from "hooks/generic/useColumnsResizing";
import ContentRow from "layout/discovery/content/ContentRow";

export const getDiscoveryDocItemsArray = (selectedDocs, checkedItems) => {
  if (selectedDocs?.length > 0 && checkedItems?.length > 0) {
    return selectedDocs.map((doc) => ({
      docName: doc.Name,
      selectedTerms: checkedItems.map((item) => item.NounPhrase),
      selectedTermsEscaped: checkedItems.map((item) => item.NounPhraseEscaped),
    }));
  }
  return [];
};

const getColumnWidths = (docView) => {
  let columnWidths = [];

  if (!docView) {
    columnWidths.push(115);
  }

  const rest = [100, 90, 90, "auto"];

  columnWidths = columnWidths.concat(rest);

  return columnWidths;
};

function getMinWidths(docView) {
  let minWidths = [];

  if (!docView) {
    minWidths.push(65);
  }

  const rest = [65, 65, 65, 300];

  minWidths = minWidths.concat(rest);

  return minWidths;
}

export const SS_KEY_FOLDER = "discovery-folder-settings";
export const SS_KEY_DOCUMENT_BY_THEME = "discovery-document-by-theme-settings";

const ContentPanel = ({ docItemsArray, docView }) => {
  const dispatch = useDispatch();
  const headerRef = useRef();
  const bodyRef = useRef();

  const { data, previousDocItemsArray: prevDocItemsArray } =
    useSelector(selectContent);
  const { byThemeContentPage: pageNumber } = useSelector(selectConfig);
  const [displayedData, setDisplayedData] = useState([]);

  const { columnWidths, onColumnResize } = useColumnsResizing(
    getColumnWidths(docView),
    getMinWidths(docView),
    docView ? SS_KEY_DOCUMENT_BY_THEME : SS_KEY_FOLDER,
    headerRef
  );

  useEffect(() => {
    if (
      docItemsArray.length > 0 &&
      !arraysEqual(docItemsArray, prevDocItemsArray)
    ) {
      const docItemsArrayWithoutEsc = docItemsArray.map(
        ({ selectedTermsEscaped, ...trimmedDocItemsArray }) =>
          trimmedDocItemsArray
      );
      dispatch(getContent(docItemsArrayWithoutEsc));
      dispatch(setPreviousDocItems(docItemsArray));
      dispatch(setByThemeContentPage(1));
    }
  }, [dispatch, docItemsArray]);

  const handlePageChange = ([from]) => {
    setDisplayedData([...data].splice(from, ITEMS_PER_PAGE + 1));
    const newPageNumber = Math.ceil(from / ITEMS_PER_PAGE) + 1;
    dispatch(setByThemeContentPage(newPageNumber));
    scrollToTop(bodyRef);
  };

  useEffect(() => {
    if (data.length) handlePageChange([0]);
  }, [data]);

  const getRows = () => {
    /* First column is optional depends on view. So array with sizes can also contain different amount of values.
    Because of this we assign sizes from last to first column. To do this need to reverse array with sizes */
    const reversedColumnWidths = [...columnWidths];
    reversedColumnWidths.reverse();

    return displayedData.map((doc) => (
      <ContentRow
        docName={doc.docName}
        legends={doc.legend}
        location={doc.location}
        docRef={doc.ref}
        text={doc.text}
        docView={docView}
        reversedColumnWidths={reversedColumnWidths}
      />
    ));
  };

  const handleScroll = (e) => {
    if (headerRef.current && bodyRef.current) {
      const { scrollLeft } = e.target;
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  return (
    <div className="content" role="presentation" data-testid="content-pane">
      <ContentHeader
        documentId={docView ? 1 : 0}
        docItemsLength={docItemsArray.length}
        paginationConfig={{
          numberOfItems: data?.length || 0,
          itemsPerPage: ITEMS_PER_PAGE,
          initialPage: pageNumber,
          onPageChange: handlePageChange,
        }}
        columnWidths={columnWidths}
        onResize={onColumnResize}
        headerRef={headerRef}
      />
      <div ref={bodyRef} className="content__body" onScroll={handleScroll}>
        <SpinnerWithLoadMask opacity={0.7} fullScreen={false} />
        {data?.length > 0 && docItemsArray.length ? getRows() : EmptyContent()}
      </div>
    </div>
  );
};

export default ContentPanel;

ContentPanel.propTypes = {
  docItemsArray: PropTypes.arrayOf(
    PropTypes.shape({
      docName: PropTypes.string,
      selectedTerms: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  docView: PropTypes.bool,
};
